import { Toaster } from "@/components/ui/sonner";
import { Toaster as ToasterShadcn } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "@/store";
import { Layout } from "@/components/layout/Layout";
import { ProductsProvider } from "@/components/products/ProductsContext";
import { LoginForm } from "@/components/auth/LoginForm";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SuperAdminRoute } from "@/components/auth/SuperAdminRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { useAuthInit } from "@/hooks/useAuthInit";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import Opportunities from "./pages/Opportunities";

import Settings from "./pages/Settings";
import OrganizationSettings from "./pages/OrganizationSettings";
import NotFound from "./pages/NotFound";
import Products from "./pages/Products";
import Roles from "./pages/Roles";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { SessionExpired } from "./pages/SessionExpired";
import Users from "./pages/Users";
import CompaniesAdmin from "./pages/CompaniesAdmin";
import WelcomePage from "./pages/Index";
import { PublicPlansPage } from "./pages/PublicPlansPage";
import { PlanRegistrationPage } from "./pages/PlanRegistrationPage";
import Reports from "./pages/Reports";
import PipelinesAdmin from "./pages/PipelinesAdmin";

const queryClient = new QueryClient();

const AuthInitializer = ({ children }: { children: React.ReactNode }) => {
  useAuthInit();
  return <>{children}</>;
};

const App = () => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <ToasterShadcn />
          {/* Usamos HashRouter para evitar errores 404 al recargar en S3 */}
          <HashRouter>
            <AuthInitializer>
              <ProductsProvider>
                <Routes>
                  {/* Rutas PÚBLICAS */}
                  <Route path="/" element={<PublicPlansPage />} />
                  <Route path="/planes" element={<PublicPlansPage />} />
                  <Route path="/precios" element={<PublicPlansPage />} />
                  <Route path="/pricing" element={<PublicPlansPage />} />

                  <Route
                    path="/register/:planId?"
                    element={<PlanRegistrationPage />}
                  />
                  <Route
                    path="/registro/:planId?"
                    element={<PlanRegistrationPage />}
                  />

                  {/* Rutas de autenticación */}
                  <Route path="/login" element={<LoginForm />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/session-expired" element={<SessionExpired />} />
                  <Route
                    path="/reset-password/:token?"
                    element={<ResetPassword />}
                  />

                  {/* Rutas PROTEGIDAS */}
                  <Route
                    path="/index"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <WelcomePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Dashboard />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/customers"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Customers />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/opportunities"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Opportunities />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/products"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Products />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Settings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Users />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/roles"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Roles />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Reports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pipelinesadmin"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PipelinesAdmin />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Rutas para ADMIN */}
                  <Route
                    path="/customers"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <Layout>
                            <Customers />
                          </Layout>
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pipelinesadmin"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <Layout>
                            <PipelinesAdmin />
                          </Layout>
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />

                  {/* Rutas para SUPER ADMIN */}

                  <Route
                    path="/admin/roles"
                    element={
                      <ProtectedRoute>
                        <SuperAdminRoute>
                          <Layout>
                            <Roles />
                          </Layout>
                        </SuperAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute>
                        <SuperAdminRoute>
                          <Layout>
                            <Users />
                          </Layout>
                        </SuperAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/companies"
                    element={
                      <ProtectedRoute>
                        <SuperAdminRoute>
                          <Layout>
                            <CompaniesAdmin />
                          </Layout>
                        </SuperAdminRoute>
                      </ProtectedRoute>
                    }
                  />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </ProductsProvider>
            </AuthInitializer>
          </HashRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </PersistGate>
  </Provider>
);

export default App;
